import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Bot, X, Send, Trash2, CheckCircle, XCircle, Loader2, MessageSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import { API_BASE_URL_EXPORT } from "@/services/api";

interface Message {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  type?: "text" | "confirm" | "tool_start";
  confirmData?: { tool: string; args: Record<string, any> };
}

export function AgentChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const scrollToBottom = () => {
    setTimeout(() => {
      scrollRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 50);
  };

  useEffect(() => {
    if (open) {
      setUnread(0);
      inputRef.current?.focus();
      scrollToBottom();
    }
  }, [open]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const addMessage = useCallback((msg: Omit<Message, "id">) => {
    const id = `${Date.now()}-${Math.random()}`;
    setMessages(prev => [...prev, { ...msg, id }]);
    if (!open) setUnread(n => n + 1);
  }, [open]);

  const streamResponse = async (endpoint: string, body: object) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const apiBase = API_BASE_URL_EXPORT;
      const response = await fetch(`${apiBase}/v1/agent/${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let assistantText = "";
      let assistantMsgId = `${Date.now()}-assistant`;

      // Add placeholder
      setMessages(prev => [...prev, { id: assistantMsgId, role: "assistant", content: "", type: "text" }]);

      while (reader) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6).trim();
          if (data === "[DONE]") break;

          try {
            const parsed = JSON.parse(data);

            if (parsed.type === "text") {
              assistantText = parsed.content;
              setMessages(prev => prev.map(m =>
                m.id === assistantMsgId ? { ...m, content: assistantText } : m
              ));
            } else if (parsed.type === "confirm") {
              // Replace placeholder with confirm card
              setMessages(prev => prev.map(m =>
                m.id === assistantMsgId
                  ? { ...m, type: "confirm", content: formatConfirmMessage(parsed.tool, parsed.args), confirmData: { tool: parsed.tool, args: parsed.args } }
                  : m
              ));
            } else if (parsed.type === "tool_start") {
              setMessages(prev => prev.map(m =>
                m.id === assistantMsgId ? { ...m, content: `Running ${parsed.tool}...` } : m
              ));
            }
          } catch {
            // ignore parse errors
          }
        }
      }
    } catch (e: any) {
      addMessage({ role: "assistant", content: "Sorry, something went wrong. Please try again.", type: "text" });
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setInput("");
    addMessage({ role: "user", content: msg, type: "text" });
    await streamResponse("chat", { message: msg });
  };

  const handleConfirm = async (tool: string, args: Record<string, any>, msgId: string) => {
    // Replace confirm card with "Confirmed" indicator
    setMessages(prev => prev.map(m =>
      m.id === msgId ? { ...m, type: "text", content: `✓ Confirmed — executing...`, confirmData: undefined } : m
    ));
    await streamResponse("confirm", { tool_name: tool, tool_args: args });
  };

  const handleDeny = (msgId: string) => {
    setMessages(prev => prev.map(m =>
      m.id === msgId ? { ...m, type: "text", content: "Cancelled.", confirmData: undefined } : m
    ));
  };

  const handleClear = async () => {
    const token = localStorage.getItem("token");
    await fetch(`${API_BASE_URL_EXPORT}/v1/agent/session`, {
      method: "DELETE",
      headers: { "Authorization": `Bearer ${token}` },
    });
    setMessages([]);
    addMessage({ role: "assistant", content: "Session cleared. How can I help you?", type: "text" });
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-float flex items-center justify-center hover:scale-105 transition-transform"
        aria-label="Open AI Assistant"
      >
        {open ? <X className="h-6 w-6" /> : <Bot className="h-6 w-6" />}
        {unread > 0 && !open && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {unread}
          </span>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[520px] bg-background border rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-primary text-primary-foreground rounded-t-2xl">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5" />
              <span className="font-semibold text-sm">StockWise Assistant</span>
              <Badge variant="secondary" className="text-xs bg-primary-foreground/20 text-primary-foreground border-0">AI</Badge>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7 text-primary-foreground hover:bg-primary-foreground/20" onClick={handleClear} title="Clear chat">
                <Trash2 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-primary-foreground hover:bg-primary-foreground/20" onClick={() => setOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 px-4 py-3">
            {messages.length === 0 && (
              <div className="text-center text-muted-foreground text-sm py-8 space-y-2">
                <Bot className="h-10 w-10 mx-auto opacity-30" />
                <p>Hi! I'm your StockWise assistant.</p>
                <p className="text-xs">Ask me to check stock, record a sale, add a product, or anything else.</p>
              </div>
            )}
            {messages.map(msg => (
              <MessageBubble
                key={msg.id}
                message={msg}
                onConfirm={(tool, args) => handleConfirm(tool, args, msg.id)}
                onDeny={() => handleDeny(msg.id)}
              />
            ))}
            {loading && messages[messages.length - 1]?.role !== "assistant" && (
              <div className="flex gap-2 mb-3">
                <div className="bg-muted rounded-2xl rounded-tl-sm px-3 py-2 text-sm flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span className="text-muted-foreground">Thinking...</span>
                </div>
              </div>
            )}
            <div ref={scrollRef} />
          </ScrollArea>

          {/* Input */}
          <div className="px-3 py-3 border-t flex gap-2">
            <Input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && handleSend()}
              placeholder="Ask me anything..."
              disabled={loading}
              className="flex-1 text-sm h-9"
            />
            <Button size="sm" onClick={handleSend} disabled={loading || !input.trim()} className="h-9 px-3">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>

          {/* WhatsApp connect */}
          <WhatsAppConnect />
        </div>
      )}
    </>
  );
}

function MessageBubble({ message, onConfirm, onDeny }: {
  message: Message;
  onConfirm: (tool: string, args: Record<string, any>) => void;
  onDeny: () => void;
}) {
  const isUser = message.role === "user";

  return (
    <div className={cn("flex mb-3", isUser ? "justify-end" : "justify-start")}>
      <div className={cn(
        "max-w-[85%] rounded-2xl px-3 py-2 text-sm",
        isUser
          ? "bg-primary text-primary-foreground rounded-tr-sm"
          : "bg-muted text-foreground rounded-tl-sm"
      )}>
        {message.type === "confirm" && message.confirmData ? (
          <div className="space-y-2">
            <p className="whitespace-pre-wrap">{message.content}</p>
            <div className="flex gap-2 pt-1">
              <Button size="sm" className="h-7 text-xs" onClick={() => onConfirm(message.confirmData!.tool, message.confirmData!.args)}>
                <CheckCircle className="h-3 w-3 mr-1" /> Confirm
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onDeny}>
                <XCircle className="h-3 w-3 mr-1" /> Cancel
              </Button>
            </div>
          </div>
        ) : (
          <p className="whitespace-pre-wrap leading-relaxed">{message.content || <span className="opacity-40">...</span>}</p>
        )}
      </div>
    </div>
  );
}

function formatConfirmMessage(tool: string, args: Record<string, any>): string {
  switch (tool) {
    case "add_product":
      return `Add product to inventory:\n• Name: ${args.name}\n• SKU: ${args.sku}\n• Selling price: KES ${args.sales_price}\n• Quantity: ${args.quantity} ${args.unit || "pcs"}\n\nConfirm?`;
    case "create_sale":
      const items = (args.items || []).map((i: any) => `  • ${i.product_name} × ${i.quantity}`).join("\n");
      return `Record sale:\n${items}\n• Payment: ${args.payment_type || "cash"}\n\nConfirm?`;
    case "add_credit_customer":
      return `Add credit customer:\n• Name: ${args.name}\n• Phone: ${args.phone}\n• Credit limit: KES ${args.credit_limit}\n\nConfirm?`;
    case "update_product_price":
      return `Update price for "${args.product_name}":\n• New selling price: KES ${args.new_sales_price || "unchanged"}\n\nConfirm?`;
    case "create_formula":
      const ings = (args.ingredients || []).map((i: any) => `  • ${i.product_name}: ${i.quantity} ${i.unit}`).join("\n");
      return `Create formula "${args.name}":\n• Output: ${args.output_quantity} ${args.output_unit || "litres"}/batch\n• Price: KES ${args.price_per_unit || "?"}/litre\n• Ingredients:\n${ings}\n\nConfirm?`;
    case "run_production":
      return `Run production:\n• Formula: ${args.formula_name}\n• Batches: ${args.batches || 1}\n${args.notes ? `• Notes: ${args.notes}\n` : ""}\nThis will deduct ingredients from stock and add bulk output.\n\nConfirm?`;
    default:
      return `Execute ${tool} with:\n${JSON.stringify(args, null, 2)}\n\nConfirm?`;
  }
}

function WhatsAppConnect() {
  const [phone, setPhone] = useState("");
  const [linked, setLinked] = useState(false);
  const [linking, setLinking] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const handleLink = async () => {
    if (!phone.trim()) return;
    setLinking(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL_EXPORT}/v1/agent/whatsapp/link`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ phone }),
      });
      if (res.ok) {
        setLinked(true);
        setShowForm(false);
      }
    } finally {
      setLinking(false);
    }
  };

  if (linked) {
    return (
      <div className="px-3 pb-2 flex items-center gap-1 text-xs text-green-600">
        <MessageSquare className="h-3 w-3" />
        WhatsApp connected — chat from your phone too
      </div>
    );
  }

  return (
    <div className="px-3 pb-2 border-t pt-2">
      {!showForm ? (
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <MessageSquare className="h-3 w-3" />
          Connect WhatsApp to chat from your phone
        </button>
      ) : (
        <div className="flex gap-2">
          <Input
            value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder="+254712345678"
            className="h-7 text-xs flex-1"
          />
          <Button size="sm" className="h-7 text-xs px-2" onClick={handleLink} disabled={linking}>
            {linking ? <Loader2 className="h-3 w-3 animate-spin" /> : "Link"}
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs px-2" onClick={() => setShowForm(false)}>
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );
}
